import { useQuery } from "@tanstack/react-query";
import { CallRecordingPlayer } from "shared/components/CallRecordingPlayer";
import { CallTranscript } from "shared/components/CallTranscript";
import { MessageContentCard } from "./MessageContentCard";
import type { NoteDetails } from "../types";
import { inboxApi } from "../api/api";
import agentProfileSvg from "shared/icons/boomLeasingAgentProfile.svg";

interface CallTranscriptContentProps {
  // AI-leasing-agent path: transcript fetched via note's call_transcript_id.
  noteDetails?: NoteDetails;
  // Outbound softphone path: transcript fetched via the BoomCRM call id.
  outboundCallId?: string;
  contactName?: string;
  // Gate the transcript fetch — defaults to on for the existing always-open
  // (AI-agent) usage; the inbox voice card passes `false` until the card is
  // expanded so a thread of completed calls doesn't fire N transcript GETs.
  enabled?: boolean;
}

export const CallTranscriptContent = ({
  noteDetails,
  outboundCallId,
  contactName = "Unknown",
  enabled = true
}: CallTranscriptContentProps) => {
  const { data, isLoading: loading } = useQuery({
    queryKey: outboundCallId
      ? ["OUTBOUND_CALL_TRANSCRIPT", outboundCallId]
      : ["CALL_TRANSCRIPT", noteDetails?.call_transcript_id],
    queryFn: () =>
      outboundCallId
        ? inboxApi.getOutboundCallTranscript(outboundCallId)
        : inboxApi.getCallTranscript(noteDetails!.call_transcript_id!),
    enabled: enabled && (!!outboundCallId || !!noteDetails?.call_transcript_id)
  });

  const transcript = data?.transcript ?? [];

  if (loading)
    return (
      <MessageContentCard isFromContact>
        <p className="text-sm text-content-200">Loading transcript...</p>
      </MessageContentCard>
    );

  return (
    <MessageContentCard
      isFromContact
      className="flex flex-col gap-4 divide-y divide-[rgba(28,29,34,0.04)] [&>*]:pt-4 [&>*:first-child]:pt-0"
    >
      {/* Agent */}
      {data?.agent_name && (
        <div className="flex items-center gap-2.5">
          <img src={agentProfileSvg} alt="Agent" className="size-9" />
          <div className="text-sm text-content-1000">
            <span className="text-content-200">Agent: </span>
            <span className="font-medium">{data.agent_name}</span>
          </div>
        </div>
      )}

      {/* Summary */}
      {data?.summary && (
        <div>
          <p className="mb-1 text-xs font-medium text-content-200">Summary</p>
          <p className="text-sm text-content-1000">{data.summary}</p>
        </div>
      )}

      {/* Recording */}
      {data?.recording_url && (
        <div>
          <p className="mb-1 text-xs font-medium text-content-200">Recording</p>
          <CallRecordingPlayer
            recordingUrl={data.recording_url}
            durationSeconds={data.recording_duration_seconds}
          />
        </div>
      )}

      {/* Transcript */}
      <CallTranscript lines={transcript} contactName={contactName} />
    </MessageContentCard>
  );
};
